import { DEFAULT_TAKE, globalScrollDistance } from "@/utils/main";
import { ErrorResponseData } from "@/types";
import { AxiosError } from "axios";
import { computed, ref } from "vue";
import { useInfiniteScroll } from "@vueuse/core";
import { useStore } from "vuex";

// Загрузка, пагинация и обработка ошибок для потребностей по Беру
export function useBeruExchange(
  action: string,
  getter: string,
  metaGetter: string,
  getScrollEl: () => HTMLElement | null | undefined,
) {
  const store = useStore();
  const pharmId = computed<number>(() => store.getters["data/pharmId"]);

  const error = ref("");
  const isLoading = ref(true);
  const skip = ref(0);
  const isLoadingMore = ref(false);
  const showLoaderMore = ref(false);
  const hasLoadMoreError = ref(false);

  const data = computed(() => store.getters[getter]);
  const meta = computed(() => store.getters[metaGetter]);

  const parseError = (e: unknown): string => {
    const axiosError = e as AxiosError<ErrorResponseData>;
    if (axiosError.status !== 500 && axiosError.response?.data) {
      return axiosError.response.data.message;
    }
    return "Ошибка загрузки данных";
  };

  const fetchInitial = async () => {
    try {
      error.value = "";
      hasLoadMoreError.value = false;
      isLoading.value = true;
      skip.value = 0;
      await store.dispatch(action, { pharmId: pharmId.value, skip: 0 });
    } catch (e) {
      error.value = parseError(e);
    } finally {
      isLoading.value = false;
    }
  };

  const loadMore = async () => {
    if (isLoadingMore.value) return;
    isLoadingMore.value = true;
    hasLoadMoreError.value = false;
    skip.value += DEFAULT_TAKE;
    const loaderTimer = setTimeout(() => {
      showLoaderMore.value = true;
    }, 1000);
    try {
      await store.dispatch(action, { pharmId: pharmId.value, skip: skip.value });
    } catch (e) {
      error.value = parseError(e);
      hasLoadMoreError.value = true;
      skip.value -= DEFAULT_TAKE;
    } finally {
      clearTimeout(loaderTimer);
      showLoaderMore.value = false;
      isLoadingMore.value = false;
    }
  };

  useInfiniteScroll(
    getScrollEl,
    loadMore,
    {
      distance: globalScrollDistance,
      canLoadMore: () => {
        if (hasLoadMoreError.value) return false;
        if (!meta.value || !data.value) return false;
        return data.value.length < meta.value.count;
      },
    },
  );

  return {
    error,
    isLoading,
    showLoaderMore,
    hasLoadMoreError,
    data,
    fetchInitial,
  };
}
